import React, { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { useQueryClient } from "react-query";
import { AgGridReact } from "ag-grid-react";
import { ColDef } from "ag-grid-enterprise";
import { Dialog, Transition } from "@headlessui/react";
import {
  AlertTriangle,
  Download,
  Eye,
  FileSpreadsheet,
  FolderOpen,
  Info,
  Layers,
  Send,
  Tag,
  Upload,
  X,
} from "lucide-react";
import QuickSearch from "../../components/QuickSearch";
import { sideBar } from "../../utils/AgGridConfig";
import {
  useListS3Files,
  usePublishPas3,
  useUploadFinancialToCaptable,
} from "../../queries/dataPipeline";
import {
  CaptableExtractionStats,
  CaptableRunSummary,
  FinancialExtractionStats,
  FinancialRunSummary,
  FinancialType,
  Stage3CompanySummary,
  cancelCaptableRun,
  isCaptableRunCancelled,
} from "../../api/dataPipeline";
import { DataPipelineFilesPopup } from "./DataPipelineFilesPopup";
import { ExcelViewerModal } from "./ExcelViewerModal";
import { BatchExtractModal } from "./BatchExtractModal";
import { Stage4BackfillModal } from "./Stage4BackfillModal";
import { getCaptableRunIssues, getFinancialRunIssues } from "./runIssues";

interface S3Row {
  cin: string;
  name: string;
  isOnboarded: boolean;
  publishedCompanyId: string | null;
  latestCaptableRun: CaptableRunSummary | null;
  latestFinancialRun: FinancialRunSummary | null;
  stage3Company: Stage3CompanySummary | null;
}

function getIssueCategoriesForRow(row: S3Row | null | undefined): string[] {
  if (!row) return [];
  const categories = [
    ...getCaptableRunIssues(row.latestCaptableRun),
    ...getFinancialRunIssues(row.latestFinancialRun),
  ].map((issue) => issue.category);
  return Array.from(new Set(categories));
}

function makeCaptableRunFilterToken(group: string, label: string): string {
  return `${group}::${label}`;
}

function parseCaptableRunFilterToken(token: string): {
  group: string;
  label: string;
} {
  const [group, ...rest] = token.split("::");
  if (!group || rest.length === 0) {
    return { group: "Other", label: token };
  }
  return { group, label: rest.join("::") };
}

function getCaptableRunFilterTokens(row: S3Row | null | undefined): string[] {
  const run = row?.latestCaptableRun;
  if (!run) {
    return [
      makeCaptableRunFilterToken("Run", "No run"),
      makeCaptableRunFilterToken("Stage", "No Stage 3"),
      makeCaptableRunFilterToken("Stage", "No Stage 4"),
      makeCaptableRunFilterToken("Issues", "No Issues"),
    ];
  }

  const tokens = new Set<string>();
  tokens.add(makeCaptableRunFilterToken("Status", run.status));
  tokens.add(
    makeCaptableRunFilterToken(
      "Stage",
      run.resultUrl ? "Has Stage 3" : "No Stage 3",
    ),
  );
  tokens.add(
    makeCaptableRunFilterToken(
      "Stage",
      run.stage4Url ? "Has Stage 4" : "No Stage 4",
    ),
  );

  const issueCategories = getCaptableRunIssues(run).map(
    (issue) => issue.category,
  );
  if (issueCategories.length === 0) {
    tokens.add(makeCaptableRunFilterToken("Issues", "No Issues"));
  } else {
    issueCategories.forEach((category) =>
      tokens.add(makeCaptableRunFilterToken("Issues", category)),
    );
  }

  return Array.from(tokens);
}

const STATUS_CLS: Record<string, string> = {
  COMPLETED: "bg-green-100 text-green-700",
  FAILED: "bg-red-100 text-red-700",
  RUNNING: "bg-blue-100 text-blue-700",
  QUEUED: "bg-gray-100 text-gray-500",
  // A cancelled run is FAILED on the server; we display it as a neutral badge.
  CANCELLED: "bg-gray-100 text-gray-600",
};

interface RunCellProps {
  run: CaptableRunSummary | FinancialRunSummary | null;
  filesLine: string;
  // Extraction-quality issues derived from the run's stats; shown as an amber
  // chip with the full list in the hover tooltip.
  issues?: string[];
  // Short label for the main result's action group (e.g. "S3"); omitted for
  // runs whose result needs no disambiguation (financials).
  resultLabel?: string;
  onView?: () => void;
  // View the generated Stage-4 onboarding workbook (captable runs only).
  onViewStage4?: () => void;
  onInfo?: () => void;
  // Cancel a QUEUED/RUNNING run. When provided, a Cancel button is shown.
  onCancel?: () => void;
  // Optimistic "Cancelling…" state while the request is in flight / settling.
  cancelling?: boolean;
}

function formatRunDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function RunCell({
  run,
  filesLine,
  issues,
  resultLabel,
  onView,
  onViewStage4,
  onInfo,
  onCancel,
  cancelling,
}: RunCellProps) {
  if (!run) return <span className="text-gray-400 italic text-xs">No run</span>;

  // A cancelled run is FAILED + error prefixed "CANCELLED:"; show it neutrally.
  const cancelled =
    run.status === "FAILED" && isCaptableRunCancelled(run.error);
  const displayStatus = cancelled ? "CANCELLED" : run.status;
  const statusCls = STATUS_CLS[displayStatus] ?? "bg-gray-100 text-gray-500";
  // Cancel only applies while the run is still in flight.
  const canCancel =
    !!onCancel && (run.status === "QUEUED" || run.status === "RUNNING");

  return (
    <div className="flex flex-col gap-0.5 py-1">
      <div className="flex flex-wrap items-center gap-2">
        {displayStatus !== "COMPLETED" && (
          <span
            className={`text-xs px-2.5 py-1 rounded-full font-medium ${statusCls}`}
          >
            {cancelling ? "Cancelling…" : cancelled ? "Cancelled" : run.status}
          </span>
        )}
        {run.phase && run.phase !== "NOTIFY" && (
          <span className="text-xs text-gray-500">{run.phase}</span>
        )}
        {run.resultUrl && (
          <span
            className="inline-flex items-center gap-0.5"
            onClick={(e) => e.stopPropagation()}
          >
            {onView ? (
              <button
                onClick={onView}
                title={`Open the ${resultLabel ?? "result"} workbook`}
                className="inline-flex items-center gap-1 rounded-full bg-blue-50 px-2.5 py-1 text-xs font-medium text-blue-700 hover:bg-blue-100"
              >
                <Eye className="w-3.5 h-3.5" />
                {resultLabel ?? "View"}
              </button>
            ) : (
              <a
                href={run.resultUrl}
                target="_blank"
                rel="noreferrer"
                title="Download result"
                className="inline-flex items-center gap-1 rounded-full bg-blue-50 px-2.5 py-1 text-xs font-medium text-blue-700 hover:bg-blue-100"
              >
                <Download className="w-3.5 h-3.5" />
                {resultLabel ?? "Download"}
              </a>
            )}
            {onView && (
              <a
                href={run.resultUrl}
                target="_blank"
                rel="noreferrer"
                title={`Download the ${resultLabel ?? "result"} workbook`}
                className="inline-flex items-center rounded-full bg-blue-50 p-1.5 text-blue-700 hover:bg-blue-100"
              >
                <Download className="w-3.5 h-3.5" />
              </a>
            )}
          </span>
        )}
        {"stage4Url" in run && run.stage4Url && (
          <span
            className="inline-flex items-center gap-0.5"
            onClick={(e) => e.stopPropagation()}
          >
            {onViewStage4 ? (
              <button
                onClick={onViewStage4}
                title="Open the generated Stage-4 onboarding workbook (draft)"
                className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-700 hover:bg-emerald-100"
              >
                <Eye className="w-3.5 h-3.5" />
                Stage 4
              </button>
            ) : (
              <a
                href={run.stage4Url}
                target="_blank"
                rel="noreferrer"
                title="Download the generated Stage-4 onboarding workbook (draft)"
                className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-700 hover:bg-emerald-100"
              >
                <Download className="w-3.5 h-3.5" />
                Stage 4
              </a>
            )}
            {onViewStage4 && (
              <a
                href={run.stage4Url}
                target="_blank"
                rel="noreferrer"
                title="Download the generated Stage-4 onboarding workbook (draft)"
                className="inline-flex items-center rounded-full bg-emerald-50 p-1.5 text-emerald-700 hover:bg-emerald-100"
              >
                <Download className="w-3.5 h-3.5" />
              </a>
            )}
          </span>
        )}
        {onInfo && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onInfo();
            }}
            className="inline-flex items-center rounded-full p-1.5 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
            title="View extraction stats"
          >
            <Info className="w-3.5 h-3.5" />
          </button>
        )}
        {canCancel && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onCancel!();
            }}
            disabled={cancelling}
            className="inline-flex items-center gap-1 rounded-full bg-red-50 px-2.5 py-1 text-xs font-medium text-red-600 hover:bg-red-100 disabled:opacity-50 disabled:cursor-not-allowed"
            title="Cancel this run"
          >
            <X className="w-3.5 h-3.5" />
            {cancelling ? "Cancelling…" : "Cancel"}
          </button>
        )}
        {issues && issues.length > 0 && (
          <span
            className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2.5 py-1 text-xs font-medium text-amber-700 cursor-default"
            title={issues.join("\n")}
          >
            <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
            {issues.length} issue{issues.length === 1 ? "" : "s"}
          </span>
        )}
      </div>
      {filesLine && <span className="text-xs text-gray-400">{filesLine}</span>}
      <span className="text-xs text-gray-400">
        {formatRunDate(run.createdAt)}
      </span>
      {run.error && (
        <span
          className={`text-xs truncate max-w-xs ${
            cancelled ? "text-gray-400" : "text-red-500"
          }`}
          title={run.error}
        >
          {run.error}
        </span>
      )}
    </div>
  );
}

function CompanyNameRender(params: any) {
  const name: string | null = params.data?.name;
  return (
    <span
      className={`truncate ${name ? "text-gray-800" : "text-gray-400 italic"}`}
      title={name ?? "Unknown"}
    >
      {name ?? "Unknown"}
    </span>
  );
}

export const DataPipelinePage: React.FC = () => {
  const gridApi = useRef<any>(null);
  const [, setRowCount] = useState(0);
  const [activeRow, setActiveRow] = useState<S3Row | null>(null);
  const [viewTarget, setViewTarget] = useState<{
    url: string;
    title: string;
    runId: string;
    cin: string;
    // Which generated artifact is open (drives where edits are saved back to).
    target?: "stage3" | "stage4";
  } | null>(null);
  const [selectedRows, setSelectedRows] = useState<S3Row[]>([]);
  const [showBatchExtract, setShowBatchExtract] = useState(false);
  const [showStage4Backfill, setShowStage4Backfill] = useState(false);
  const [publishResult, setPublishResult] = useState<
    "success" | "error" | null
  >(null);
  const [captableStatsPopup, setCaptableStatsPopup] =
    useState<CaptableExtractionStats | null>(null);
  const [financialStatsPopup, setFinancialStatsPopup] =
    useState<FinancialExtractionStats | null>(null);
  // Stage-3 company shown in the "previously existing" info popup.
  const [stage3InfoPopup, setStage3InfoPopup] =
    useState<Stage3CompanySummary | null>(null);

  const queryClient = useQueryClient();
  const { data, isLoading, isError } = useListS3Files();

  // Runs the user has clicked Cancel on, keyed by runId. Drives the optimistic
  // "Cancelling…" badge until the run settles to a terminal status.
  const [cancellingRunIds, setCancellingRunIds] = useState<Set<string>>(
    new Set(),
  );

  const handleCancelCaptableRun = async (runId: string) => {
    setCancellingRunIds((prev) => new Set(prev).add(runId));
    try {
      await cancelCaptableRun(runId);
    } catch {
      // Idempotent + server-authoritative; on error drop the optimistic flag so
      // the real status shows and the user can retry.
      setCancellingRunIds((prev) => {
        const next = new Set(prev);
        next.delete(runId);
        return next;
      });
      return;
    }
    // A QUEUED run cancels immediately; a RUNNING one stops at the next phase
    // boundary. Poll the listing until it reaches FAILED, then drop the flag.
    queryClient.invalidateQueries("listS3Files");
  };
  const { mutate: publishCompanies, isLoading: isPublishing } =
    usePublishPas3();
  const [financialType, setFinancialType] =
    useState<FinancialType>("CONSOLIDATED");
  const [uploadFinancialResult, setUploadFinancialResult] = useState<
    "success" | "error" | null
  >(null);
  const { mutate: uploadFinancialToCaptable, isLoading: isUploadingFinancial } =
    useUploadFinancialToCaptable();

  // Publish / upload-financials require a COMPLETED captable run; batch
  // extraction doesn't, so row selection is open and we filter here instead.
  const publishableCins = selectedRows
    .filter((r) => r.latestCaptableRun?.status === "COMPLETED")
    .map((r) => r.cin);

  // Old runs that completed before Stage 4 was auto-generated (or whose fill
  // failed) — no Stage-4 workbook yet, so they're eligible for a manual backfill.
  const stage4BackfillEligibleCount = selectedRows.filter(
    (r) =>
      r.latestCaptableRun?.status === "COMPLETED" &&
      !r.latestCaptableRun?.stage4Url,
  ).length;

  const handleUploadFinancialToCaptable = () => {
    if (!publishableCins.length) return;
    setUploadFinancialResult(null);
    uploadFinancialToCaptable(
      { cins: publishableCins, financialType },
      {
        onSuccess: () => setUploadFinancialResult("success"),
        onError: () => setUploadFinancialResult("error"),
      },
    );
  };

  const rowData = useMemo<S3Row[]>(() => {
    const keys = data?.keys ?? [];
    // Most recently run company first; companies with no runs at the bottom.
    const latestRunTime = (entry: (typeof keys)[number]): number =>
      Math.max(
        entry.latestCaptableRun
          ? new Date(entry.latestCaptableRun.createdAt).getTime()
          : 0,
        entry.latestFinancialRun
          ? new Date(entry.latestFinancialRun.createdAt).getTime()
          : 0,
      );
    return keys
      .map((entry) => ({
        cin: entry.cin,
        name: entry.name ?? "",
        isOnboarded: entry.isOnboarded ?? false,
        publishedCompanyId: entry.publishedCompanyId ?? null,
        latestCaptableRun: entry.latestCaptableRun ?? null,
        latestFinancialRun: entry.latestFinancialRun ?? null,
        stage3Company: entry.stage3Company ?? null,
        runTime: latestRunTime(entry),
      }))
      .sort((a, b) => b.runTime - a.runTime);
  }, [data]);

  // While any run is being cancelled, poll the listing and clear the optimistic
  // flag once that run reaches a terminal status (FAILED — cancelled lands here).
  useEffect(() => {
    if (cancellingRunIds.size === 0) return undefined;

    setCancellingRunIds((prev) => {
      let changed = false;
      const next = new Set(prev);
      Array.from(prev).forEach((id) => {
        const run = rowData.find(
          (r) => r.latestCaptableRun?.id === id,
        )?.latestCaptableRun;
        // Drop the flag once settled, or if the run is no longer in the listing.
        if (!run || run.status === "COMPLETED" || run.status === "FAILED") {
          next.delete(id);
          changed = true;
        }
      });
      return changed ? next : prev;
    });

    const interval = setInterval(() => {
      queryClient.invalidateQueries("listS3Files");
    }, 5000);
    return () => clearInterval(interval);
  }, [cancellingRunIds, rowData, queryClient]);

  const handleSelectionChanged = () => {
    const selected: S3Row[] = gridApi.current?.api?.getSelectedRows?.() ?? [];
    setSelectedRows(selected);
  };

  const handlePublish = () => {
    if (!publishableCins.length) return;
    setPublishResult(null);
    publishCompanies(publishableCins, {
      onSuccess: () => {
        setPublishResult("success");
        gridApi.current?.api?.deselectAll();
      },
      onError: () => setPublishResult("error"),
    });
  };

  const FilesCell = (params: any) => (
    <button
      onClick={() => setActiveRow(params.data)}
      className="inline-flex items-center gap-1 px-3 py-1 text-sm text-blue-600 border border-blue-500 hover:bg-blue-50 rounded"
    >
      <FolderOpen className="w-4 h-4" />
      View Files
    </button>
  );

  const CaptableRunCell = (params: any) => {
    const run: CaptableRunSummary | null =
      params.data?.latestCaptableRun ?? null;
    const parts: string[] = [];
    if (run?.pas3Keys?.length) parts.push(`${run.pas3Keys.length} PAS-3`);
    if (run?.mgt7Key) parts.push("1 MGT-7");
    return (
      <RunCell
        run={run}
        filesLine={parts.join(" · ")}
        issues={getCaptableRunIssues(run).map((i) => i.detail)}
        resultLabel="Stage 3"
        onView={
          run?.resultUrl
            ? () =>
                setViewTarget({
                  url: run.resultUrl!,
                  title: `Captable – ${params.data?.name || params.data?.cin}`,
                  runId: run.id,
                  cin: params.data?.cin,
                  target: "stage3",
                })
            : undefined
        }
        onViewStage4={
          run?.stage4Url
            ? () =>
                setViewTarget({
                  url: run.stage4Url!,
                  title: `Stage 4 – ${params.data?.name || params.data?.cin}`,
                  runId: run.id,
                  cin: params.data?.cin,
                  target: "stage4",
                })
            : undefined
        }
        onInfo={
          run?.extractionStats
            ? () => setCaptableStatsPopup(run.extractionStats)
            : undefined
        }
        onCancel={run ? () => handleCancelCaptableRun(run.id) : undefined}
        cancelling={!!run && cancellingRunIds.has(run.id)}
      />
    );
  };

  const FinancialRunCell = (params: any) => {
    const run: FinancialRunSummary | null =
      params.data?.latestFinancialRun ?? null;
    const filesLine = run?.aoc4Keys?.length
      ? `${run.aoc4Keys.length} AOC-4`
      : "";
    return (
      <RunCell
        run={run}
        filesLine={filesLine}
        issues={getFinancialRunIssues(run).map((i) => i.detail)}
        onInfo={
          run?.extractionStats
            ? () => setFinancialStatsPopup(run.extractionStats)
            : undefined
        }
      />
    );
  };

  // Combined issue chip for the filterable Issues column; the tooltip groups
  // the file-level details per run type.
  const IssuesCell = (params: any) => {
    const captable = getCaptableRunIssues(
      params.data?.latestCaptableRun ?? null,
    );
    const financial = getFinancialRunIssues(
      params.data?.latestFinancialRun ?? null,
    );
    const total = captable.length + financial.length;
    if (total === 0) {
      return <span className="text-xs text-gray-300">—</span>;
    }
    const lines: string[] = [];
    if (captable.length) {
      lines.push("Captable:", ...captable.map((i) => `• ${i.detail}`));
    }
    if (financial.length) {
      lines.push("Financial:", ...financial.map((i) => `• ${i.detail}`));
    }
    return (
      <span
        className="inline-flex items-center gap-1 px-1.5 py-0.5 text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded cursor-default"
        title={lines.join("\n")}
      >
        <AlertTriangle className="w-3 h-3 flex-shrink-0" />
        {total} issue{total === 1 ? "" : "s"}
      </span>
    );
  };

  const columnDefs: ColDef[] = useMemo(
    () => [
      {
        headerName: "CIN",
        field: "cin",
        filter: "agTextColumnFilter",
        sortable: true,
        minWidth: 190,
        tooltipField: "cin",
      },
      {
        headerName: "Company Name",
        field: "name",
        filter: "agTextColumnFilter",
        sortable: true,
        minWidth: 220,
        cellRenderer: CompanyNameRender,
        tooltipField: "name",
      },
      {
        headerName: "Is Onboarded",
        field: "isOnboarded",
        filter: "agTextColumnFilter",
        sortable: true,
        minWidth: 180,
        cellRenderer: (params: any) => {
          const val: boolean = params.data?.isOnboarded ?? false;
          return (
            <span
              className={`text-xs px-1.5 py-0.5 rounded font-medium ${val ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"}`}
            >
              {val ? "Yes" : "No"}
            </span>
          );
        },
      },
      {
        headerName: "Company ID",
        valueGetter: (p) =>
          p.data?.publishedCompanyId ?? p.data?.stage3Company?.id ?? "",
        filter: "agTextColumnFilter",
        sortable: true,
        minWidth: 220,
        cellRenderer: (params: any) => {
          const publishedId: string | null =
            params.data?.publishedCompanyId ?? null;
          const stage3: Stage3CompanySummary | null =
            params.data?.stage3Company ?? null;
          const id = publishedId ?? stage3?.id ?? null;
          if (!id) {
            return <span className="text-xs text-gray-400 italic">—</span>;
          }
          return (
            <div className="flex items-center gap-1.5">
              <span className="text-xs font-mono text-gray-700" title={id}>
                {id}
              </span>
              {!publishedId && stage3 && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setStage3InfoPopup(stage3);
                  }}
                  className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-amber-100 text-amber-700 hover:bg-amber-200"
                  title="Previously existing"
                >
                  <Tag className="w-3 h-3" />
                </button>
              )}
            </div>
          );
        },
      },
      {
        headerName: "Files",
        sortable: false,
        filter: false,
        minWidth: 180,
        cellRenderer: FilesCell,
      },
      {
        headerName: "Captable Run",
        valueGetter: (p) => p.data?.latestCaptableRun?.status ?? "",
        // Use set-filter tags so this column can filter by Stage-3/4 presence
        // and captable issue category directly from the column filter menu.
        filterValueGetter: (p) => getCaptableRunFilterTokens(p.data),
        filter: "agSetColumnFilter",
        filterParams: {
          buttons: ["reset"],
          treeList: true,
          treeListPathGetter: (value: string | null) => {
            if (!value) return ["Other", "(Blanks)"];
            const { group, label } = parseCaptableRunFilterToken(value);
            return [group, label];
          },
          valueFormatter: (params: any) => {
            if (typeof params?.value !== "string") return params?.value ?? "";
            return parseCaptableRunFilterToken(params.value).label;
          },
        },
        sortable: true,
        minWidth: 260,
        flex: 1,
        cellRenderer: CaptableRunCell,
      },
      {
        headerName: "Financial Run",
        valueGetter: (p) => p.data?.latestFinancialRun?.status ?? "",
        filter: "agTextColumnFilter",
        sortable: true,
        minWidth: 280,
        flex: 1,
        cellRenderer: FinancialRunCell,
      },
      {
        headerName: "Issues",
        // Issue categories from both runs; the set filter treats the array as
        // one entry per category, so rows can be filtered by issue type (and
        // "(Blanks)" selects issue-free rows).
        valueGetter: (p) => getIssueCategoriesForRow(p.data),
        filter: "agSetColumnFilter",
        sortable: true,
        // Sort by how many issue categories a row has.
        comparator: (a: string[], b: string[]) =>
          (a?.length ?? 0) - (b?.length ?? 0),
        minWidth: 170,
        cellRenderer: IssuesCell,
      },
    ],
    [],
  );

  const defaultColDef = useMemo<ColDef>(
    () => ({
      sortable: true,
      autoHeight: true,
      wrapHeaderText: true,
      suppressColumnVirtualisation: true,
      filterParams: {
        buttons: ["reset"],
        maxNumConditions: 5,
      },
      minWidth: 150,
      filter: true,
      resizable: true,
      flex: 1,
    }),
    [],
  );

  const gridOptions = {
    suppressRowVirtualisation: true,
    paginationAutoPageSize: false,
    suppressScrollOnNewData: true,
  };

  const onFilterChanged = () => {
    const count = gridApi?.current?.api?.getDisplayedRowCount();
    setRowCount(count);
  };

  const handleQuickFilter = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (!gridApi.current?.api) return;
    gridApi.current.api.setGridOption("quickFilterText", value);
    gridApi.current.api.onFilterChanged();
  };

  return (
    <div className="h-[calc(100vh-15vh)]">
      <div className="flex flex-row justify-between pt-4">
        <div>
          <h1 className="mt-4 text-xl text-secondary font-bold">
            Data Pipeline
          </h1>
          <p className="text-xs text-gray-500 font-semibold">
            {gridApi?.current?.api?.getDisplayedRowCount() ?? rowData.length}{" "}
            Companies
          </p>
        </div>
        <div className="justify-end flex flex-row py-2 items-center gap-4">
          {selectedRows.length > 0 && (
            <>
              <button
                onClick={() => setShowBatchExtract(true)}
                className="inline-flex items-center gap-2 px-4 py-1.5 text-sm font-medium text-white bg-violet-600 hover:bg-violet-700 rounded whitespace-nowrap"
              >
                <Layers className="w-4 h-4 flex-shrink-0" />
                Batch Extract ({selectedRows.length})
              </button>
              <div className="inline-flex items-center gap-2">
                <select
                  value={financialType}
                  onChange={(e) =>
                    setFinancialType(e.target.value as FinancialType)
                  }
                  className="text-xs border border-gray-300 rounded px-2 py-1.5 text-gray-700 bg-white"
                >
                  <option value="CONSOLIDATED">Consolidated</option>
                  <option value="STANDALONE">Standalone</option>
                </select>
                <button
                  onClick={handleUploadFinancialToCaptable}
                  disabled={isUploadingFinancial || !publishableCins.length}
                  title={
                    publishableCins.length
                      ? undefined
                      : "Requires a completed captable run"
                  }
                  className="inline-flex items-center gap-2 px-4 py-1.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-60 rounded whitespace-nowrap"
                >
                  <Upload className="w-4 h-4 flex-shrink-0" />
                  {isUploadingFinancial
                    ? "Uploading…"
                    : `Upload Financials (${publishableCins.length})`}
                </button>
              </div>
              <button
                onClick={handlePublish}
                disabled={isPublishing || !publishableCins.length}
                title={
                  publishableCins.length
                    ? undefined
                    : "Requires a completed captable run"
                }
                className="inline-flex items-center gap-2 px-4 py-1.5 text-sm font-medium text-white bg-green-600 hover:bg-green-700 disabled:opacity-60 rounded whitespace-nowrap"
              >
                <Send className="w-4 h-4 flex-shrink-0" />
                {isPublishing
                  ? "Publishing…"
                  : `Publish (${publishableCins.length})`}
              </button>
              <button
                onClick={() => setShowStage4Backfill(true)}
                disabled={!stage4BackfillEligibleCount}
                title={
                  stage4BackfillEligibleCount
                    ? undefined
                    : "Requires a completed captable run without a Stage-4 workbook yet"
                }
                className="inline-flex items-center gap-2 px-4 py-1.5 text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 rounded whitespace-nowrap"
              >
                <FileSpreadsheet className="w-4 h-4 flex-shrink-0" />
                Generate Stage 4 ({stage4BackfillEligibleCount})
              </button>
            </>
          )}
          <QuickSearch
            placeholder={`Search`}
            className=""
            onChange={handleQuickFilter}
          />
        </div>
      </div>
      {publishResult === "success" && (
        <p className="text-sm text-green-600 mt-1">
          Companies published successfully.
        </p>
      )}
      {publishResult === "error" && (
        <p className="text-sm text-red-500 mt-1">
          Failed to publish companies. Please try again.
        </p>
      )}
      {uploadFinancialResult === "success" && (
        <p className="text-sm text-green-600 mt-1">
          Financials uploaded to captable successfully.
        </p>
      )}
      {uploadFinancialResult === "error" && (
        <p className="text-sm text-red-500 mt-1">
          Failed to upload financials. Please try again.
        </p>
      )}
      {isError && (
        <p className="text-sm text-red-500 mt-2">Failed to load S3 files.</p>
      )}
      {showBatchExtract && (
        <BatchExtractModal
          companies={selectedRows.map((r) => ({
            cin: r.cin,
            name: r.name || null,
          }))}
          onClose={() => setShowBatchExtract(false)}
        />
      )}
      {showStage4Backfill && (
        <Stage4BackfillModal
          companies={selectedRows.map((r) => ({
            cin: r.cin,
            name: r.name || null,
            latestCaptableRun: r.latestCaptableRun,
          }))}
          onClose={() => setShowStage4Backfill(false)}
        />
      )}
      {activeRow && (
        <DataPipelineFilesPopup
          cin={activeRow.cin}
          companyName={activeRow.name || null}
          showPopup={true}
          onClose={() => setActiveRow(null)}
          latestCaptableRun={
            rowData.find((r) => r.cin === activeRow.cin)?.latestCaptableRun ??
            null
          }
        />
      )}
      {viewTarget && (
        <ExcelViewerModal
          url={viewTarget.url}
          title={viewTarget.title}
          runId={viewTarget.runId}
          cin={viewTarget.cin}
          target={viewTarget.target}
          onSaved={() => queryClient.invalidateQueries("listS3Files")}
          onClose={() => setViewTarget(null)}
        />
      )}
      {stage3InfoPopup && (
        <Transition.Root show={!!stage3InfoPopup} as={Fragment}>
          <Dialog
            as="div"
            className="relative z-20"
            onClose={() => setStage3InfoPopup(null)}
          >
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-gray-500 bg-opacity-60 transition-opacity" />
            </Transition.Child>
            <div className="fixed inset-0 z-10 flex items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-200"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-150"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-sm rounded-lg bg-white shadow-xl">
                  <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
                    <Dialog.Title className="text-sm font-semibold text-gray-900">
                      Previously Existing Company
                    </Dialog.Title>
                    <button
                      type="button"
                      onClick={() => setStage3InfoPopup(null)}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="px-4 py-3 space-y-2 text-xs text-gray-700">
                    <p className="text-gray-500">
                      This company already exists in stage{" "}
                      {stage3InfoPopup.stage} and was not published from this
                      pipeline.
                    </p>
                    <div className="bg-gray-50 rounded p-2 space-y-1">
                      <div>
                        <span className="text-gray-500">Name: </span>
                        <span className="font-medium">
                          {stage3InfoPopup.name}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-500">CIN: </span>
                        <span className="font-mono">{stage3InfoPopup.cin}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Company ID: </span>
                        <span className="font-mono">{stage3InfoPopup.id}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Stage: </span>
                        <span className="font-medium">
                          {stage3InfoPopup.stage}
                        </span>
                      </div>
                    </div>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </Dialog>
        </Transition.Root>
      )}
      {captableStatsPopup && (
        <Transition.Root show={!!captableStatsPopup} as={Fragment}>
          <Dialog
            as="div"
            className="relative z-20"
            onClose={() => setCaptableStatsPopup(null)}
          >
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-gray-500 bg-opacity-60 transition-opacity" />
            </Transition.Child>
            <div className="fixed inset-0 z-10 flex items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-200"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-150"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-md rounded-lg bg-white shadow-xl flex flex-col max-h-[85vh]">
                  <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 flex-shrink-0">
                    <Dialog.Title className="text-sm font-semibold text-gray-900">
                      Extraction Stats
                    </Dialog.Title>
                    <button
                      type="button"
                      onClick={() => setCaptableStatsPopup(null)}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="px-4 py-3 space-y-3 text-xs text-gray-700 overflow-y-auto">
                    {captableStatsPopup.mgt7 && (
                      <div>
                        <div className="font-medium text-gray-900 mb-1">
                          MGT-7
                        </div>
                        <div className="bg-gray-50 rounded p-2 space-y-1">
                          <div className="truncate text-gray-500">
                            {captableStatsPopup.mgt7.file}
                          </div>
                          <div className="flex gap-4">
                            <span>
                              <span className="font-medium">
                                {captableStatsPopup.mgt7.shareholders}
                              </span>{" "}
                              shareholders
                            </span>
                            <span>
                              Individual list:{" "}
                              <span className="font-medium">
                                {captableStatsPopup.mgt7.providesIndividualList
                                  ? "Yes"
                                  : "No"}
                              </span>
                            </span>
                          </div>
                        </div>
                      </div>
                    )}
                    {captableStatsPopup.pas3s.length > 0 && (
                      <div>
                        <div className="font-medium text-gray-900 mb-1">
                          PAS-3 ({captableStatsPopup.pas3s.length})
                        </div>
                        <div className="space-y-2">
                          {captableStatsPopup.pas3s.map((p, i) => (
                            <div
                              key={i}
                              className="bg-gray-50 rounded p-2 space-y-1"
                            >
                              <div className="truncate text-gray-500">
                                {p.file}
                              </div>
                              <div className="flex flex-wrap gap-x-4 gap-y-0.5">
                                <span>
                                  <span className="font-medium">
                                    {p.rounds}
                                  </span>{" "}
                                  round{p.rounds !== 1 ? "s" : ""}
                                </span>
                                <span>
                                  <span className="font-medium">
                                    {p.allottees}
                                  </span>{" "}
                                  allottees
                                </span>
                                {p.placeholders > 0 && (
                                  <span className="text-amber-600">
                                    <span className="font-medium">
                                      {p.placeholders}
                                    </span>{" "}
                                    placeholder{p.placeholders !== 1 ? "s" : ""}
                                  </span>
                                )}
                                <span>
                                  <span className="font-medium">{p.loas}</span>{" "}
                                  LOA{p.loas !== 1 ? "s" : ""}
                                </span>
                              </div>
                              {p.loaIssues.length > 0 && (
                                <ul className="mt-1 space-y-0.5">
                                  {p.loaIssues.map((issue, j) => (
                                    <li
                                      key={j}
                                      className="text-amber-700 bg-amber-50 rounded px-2 py-0.5"
                                    >
                                      {issue}
                                    </li>
                                  ))}
                                </ul>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    <div className="flex gap-6 pt-1 border-t border-gray-100">
                      <span>
                        Stage 3 rows:{" "}
                        <span className="font-medium">
                          {captableStatsPopup.stage3Rows}
                        </span>
                      </span>
                      <span>
                        Allotment rows:{" "}
                        <span className="font-medium">
                          {captableStatsPopup.allotmentRows}
                        </span>
                      </span>
                    </div>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </Dialog>
        </Transition.Root>
      )}
      {financialStatsPopup && (
        <Transition.Root show={!!financialStatsPopup} as={Fragment}>
          <Dialog
            as="div"
            className="relative z-20"
            onClose={() => setFinancialStatsPopup(null)}
          >
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-gray-500 bg-opacity-60 transition-opacity" />
            </Transition.Child>
            <div className="fixed inset-0 z-10 flex items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-200"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-150"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-md rounded-lg bg-white shadow-xl flex flex-col max-h-[85vh]">
                  <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 flex-shrink-0">
                    <Dialog.Title className="text-sm font-semibold text-gray-900">
                      Financial Extraction Stats
                    </Dialog.Title>
                    <button
                      type="button"
                      onClick={() => setFinancialStatsPopup(null)}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="px-4 py-3 space-y-3 text-xs text-gray-700 overflow-y-auto">
                    <div className="flex gap-6 pb-2 border-b border-gray-100">
                      <span>
                        Total:{" "}
                        <span className="font-medium">
                          {financialStatsPopup.totalFiles}
                        </span>
                      </span>
                      <span className="text-green-700">
                        Success:{" "}
                        <span className="font-medium">
                          {financialStatsPopup.successfulFiles}
                        </span>
                      </span>
                      {financialStatsPopup.failedFiles > 0 && (
                        <span className="text-red-600">
                          Failed:{" "}
                          <span className="font-medium">
                            {financialStatsPopup.failedFiles}
                          </span>
                        </span>
                      )}
                    </div>
                    <div className="space-y-2">
                      {financialStatsPopup.files.map((f, i) => (
                        <div
                          key={i}
                          className="bg-gray-50 rounded p-2 space-y-1"
                        >
                          <div className="truncate text-gray-500">{f.file}</div>
                          <div className="flex flex-wrap gap-x-4 gap-y-0.5">
                            <span>
                              <span className="font-medium">
                                {f.financialYear}
                              </span>
                            </span>
                            <span className="capitalize text-gray-500">
                              {f.method}
                            </span>
                            <span>
                              BS:{" "}
                              <span
                                className={
                                  f.hasBalanceSheet
                                    ? "font-medium text-green-700"
                                    : "text-red-500"
                                }
                              >
                                {f.hasBalanceSheet ? "Yes" : "No"}
                              </span>
                            </span>
                            <span>
                              P&L:{" "}
                              <span
                                className={
                                  f.hasProfitAndLoss
                                    ? "font-medium text-green-700"
                                    : "text-red-500"
                                }
                              >
                                {f.hasProfitAndLoss ? "Yes" : "No"}
                              </span>
                            </span>
                            {f.nullFields > 0 && (
                              <span className="text-amber-600">
                                <span className="font-medium">
                                  {f.nullFields}
                                </span>{" "}
                                null fields
                              </span>
                            )}
                          </div>
                          {f.error && (
                            <div
                              className="text-red-500 truncate"
                              title={f.error}
                            >
                              {f.error}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </Dialog>
        </Transition.Root>
      )}
      <div className="ag-theme-alpine pb-4 flex mt-4" style={{ height: "90%" }}>
        <AgGridReact
          columnDefs={columnDefs}
          rowData={rowData}
          ref={gridApi}
          onFilterChanged={onFilterChanged}
          cellSelection={true}
          pagination={true}
          enableCellTextSelection={true}
          paginationPageSize={20}
          rowSelection={{
            mode: "multiRow",
            checkboxes: true,
            headerCheckbox: true,
            selectAll: "filtered",
          }}
          onSelectionChanged={handleSelectionChanged}
          onGridReady={(params) => {
            gridApi.current = params;
          }}
          alwaysMultiSort
          defaultColDef={defaultColDef}
          className="w-full col"
          sideBar={sideBar}
          gridOptions={gridOptions}
          loading={isLoading}
          getRowStyle={(params) => {
            if (params.rowIndex % 2 === 0) {
              return { background: "#f8f8f8" };
            } else {
              return { background: "#ffffff" };
            }
          }}
        />
      </div>
    </div>
  );
};
